import React, { useEffect, useRef, useState, memo } from "react";
import { Box, Divider, Typography, TextField, IconButton, Tooltip } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomTable from "../../../../../../../shared/components/table/custom-table";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { useParams } from "react-router-dom";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { LoaderComponent } from "../../../../../../../shared/components/loaders/page-loader";
import {
  filterCertifiedIncomingRawMaterials,
  filterIncomingRawMaterialsForSource,
  isCertifiedIncomingRawMaterial,
  mergeIncomingRawMaterialsForDisplay,
  withIncomingRawMaterialKeys,
} from "../incoming-raw-material.utils";
import { formatTcV4Decimal, roundTcV4Decimal } from "../../../../tc-v4-decimals";

const READABLE_HEADER_TABLE_SX = {
  width: "100%",
  p: 2,
  "& .MuiTableHead-root .MuiTableRow-root": {
    height: "auto",
  },
  "& .MuiTableHead-root .MuiTableCell-head, & .MuiTableHead-root .MuiTableCell-head *": {
    whiteSpace: "normal !important",
  },
  "& .MuiTableHead-root .MuiTableCell-head": {
    minHeight: 64,
    height: "auto",
    alignItems: "flex-start",
  },
  "& .Mui-TableHeadCell-Content": {
    alignItems: "flex-start !important",
  },
  "& .Mui-TableHeadCell-Content-Labels": {
    minWidth: 0,
    alignItems: "flex-start !important",
  },
  "& .Mui-TableHeadCell-Content-Wrapper": {
    overflow: "visible !important",
    textOverflow: "clip !important",
    lineHeight: 1.25,
    wordBreak: "normal",
  },
  "& .Mui-TableHeadCell-Content-Actions": {
    flexShrink: 0,
    alignSelf: "flex-start",
  },
} as const;

// Module-level request coalescer: identical in-flight requests (same endpoint +
// params) share ONE network call regardless of which component instance, mount,
// or re-render triggered them. The incoming stepper can request the same
// incoming-tc-info row for raw materials and maximum weight, so both consumers
// share one request. Keyed
// by the full request signature and evicted shortly after settle, so a later
// genuinely-new fetch still runs (86ajcn0z8).
const inFlightRequests = new Map<string, Promise<any>>();
// Hold a settled result briefly so the fallback chain in one dialog open
// (max-weight and raw-material consumers) — which can span a couple of seconds
// of UAT latency — reuses the same call instead of re-issuing
// it. Short enough that a later genuinely-new fetch (reopen/navigate) still runs.
const COALESCE_TTL_MS = 5000;
const coalesceRequest = <T,>(key: string, run: () => Promise<T>): Promise<T> => {
  const existing = inFlightRequests.get(key);
  if (existing) return existing as Promise<T>;
  const request = run().finally(() => {
    setTimeout(() => inFlightRequests.delete(key), COALESCE_TTL_MS);
  });
  inFlightRequests.set(key, request);
  return request;
};

interface ProductShipmentResponse {
  tcProductNo: string;
}

interface RawMaterialDetail {
  incomingStandard?: string;
  tcProductCategoryCode: string;
  tcProductDetailCode: string;
  rawMaterialCode: string;
  rawMaterialPercentage: number;
  rawMaterialWeight?: number;
  calculatedWeight: string;
  calculationTrace?: string[];
  rawMaterialKey?: string;
  isCertified?: string;
  tcProductNetShippingWeight?: number;
  tcProductSupplementaryWeight?: number;
  tcProductAdditivesWeight?: number;
}

interface ComponentInfoProps {
  formRef: React.Ref<any>;
  componentData: any;
  setComponentData: (data: any) => void;
  selectedArticles?: string[];
  incomingTcId?: string; 
  incomingAtcId?: string; 
  incomingLedgerTransactionId? : string;
  incomingTcType?: string;
  standardsByArticle?: { [productNo: string]: string[] };
}

const toNumber = (value: any): number => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const rawMaterialKey = (item: Partial<RawMaterialDetail>, index: number): string =>
  [
    item.tcProductCategoryCode || "",
    item.tcProductDetailCode || "",
    item.rawMaterialCode || "",
    Number(item.rawMaterialPercentage || 0),
    index,
  ].join("|");

const unwrapIncomingProducts = (payload: any): any[] => {
  if (!payload) return [];
  let data = payload;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (data?.response !== undefined) return unwrapIncomingProducts(data.response);
  const rows = Array.isArray(data) ? data : [data];
  return rows.flatMap((row: any) => {
    const value = row?.trackitPortalData ?? row;
    return Array.isArray(value) ? value : [value];
  }).filter(Boolean);
};

const mapIncomingProductToRawDetails = (
  product: any,
  sourceType: any
): Omit<RawMaterialDetail, "calculatedWeight">[] => {
  const rawMaterials = Array.isArray(product?.tcProductRawMaterialInformation)
    ? product.tcProductRawMaterialInformation
    : [];
  const netWeight = toNumber(product?.tcProductNetShippingWeight);
  const supplementaryWeight = toNumber(product?.tcProductSupplementaryWeight);
  const additivesWeight = toNumber(product?.tcProductAdditivesWeight);

  const mappedRows = rawMaterials.map((item: any) => {
    const detail = {
      tcProductCategoryCode: product?.tcProductCategoryCode || "",
      tcProductDetailCode: product?.tcProductDetailCode || "",
      rawMaterialCode: item?.tcProductRawMaterialCode || item?.rawMaterialCode || "",
      rawMaterialPercentage: toNumber(item?.tcProductRawMaterialPercentage || item?.rawMaterialPercentage) || 100,
      isCertified: item?.isCertified ?? item?.is_certified ?? "",
      tcProductNetShippingWeight: netWeight,
      tcProductSupplementaryWeight: supplementaryWeight,
      tcProductAdditivesWeight: additivesWeight,
    };
    return detail;
  });
  return filterIncomingRawMaterialsForSource(
    withIncomingRawMaterialKeys(mappedRows),
    sourceType
  );
};

const calculateSplitDetails = (netWeightUsed: number, percentage: number, item: Partial<RawMaterialDetail>) => {
  const sourceNet = toNumber(item.tcProductNetShippingWeight);
  const sourceSupplementary = toNumber(item.tcProductSupplementaryWeight);
  const sourceAdditives = toNumber(item.tcProductAdditivesWeight);
  const supplementaryUsed = sourceNet > 0 ? (sourceSupplementary / sourceNet) * netWeightUsed : 0;
  const additivesUsed = sourceNet > 0 ? (sourceAdditives / sourceNet) * netWeightUsed : 0;
  const productWeightForSplit = Math.max(netWeightUsed - supplementaryUsed - additivesUsed, 0);
  const calculatedWeight = roundTcV4Decimal(
    (toNumber(percentage) / 100) * productWeightForSplit
  );

  return {
    sourceNet,
    sourceSupplementary,
    sourceAdditives,
    supplementaryUsed,
    additivesUsed,
    productWeightForSplit,
    calculatedWeight,
  };
};

const formatStandards = (standards: any): string => {
  const values = Array.isArray(standards) ? standards : standards ? [standards] : [];
  const filtered = values
    .map((standard) => String(standard || "").trim())
    .filter((standard) => standard && standard.toLowerCase() !== "na");
  return filtered.length > 0 ? Array.from(new Set(filtered)).join(", ") : "N/A";
};

const RenderRawMaterialPanel = memo(({ row, rawMaterialDetails, loading, directWeightMode, rawMaterialWeights, onDirectWeightChange }: {
  row: { original: ProductShipmentResponse },
  rawMaterialDetails: RawMaterialDetail[] | undefined,
  loading?: boolean,
  directWeightMode?: boolean,
  rawMaterialWeights?: { [rawMaterialKey: string]: string },
  onDirectWeightChange?: (rawMaterialKey: string, value: string) => void,
}) => {
  // The column definitions must stay referentially stable: recreating them on
  // every keystroke remounts the weight TextField (new Cell identity), whose
  // MUI mount effect updates FormControl state and loops render->remount until
  // React's maximum update depth. Latest values are read through refs instead.
  const weightsRef = React.useRef(rawMaterialWeights);
  weightsRef.current = rawMaterialWeights;
  const changeRef = React.useRef(onDirectWeightChange);
  changeRef.current = onDirectWeightChange;

  const rawMaterialColumns = React.useMemo(() => [
    { accessorKey: "incomingStandard", header: "Incoming TC Standard", size: 190, minSize: 170, maxSize: 240 },
    { accessorKey: "tcProductCategoryCode", header: "Incoming Product Category Code", size: 250, minSize: 210, maxSize: 290 },
    { accessorKey: "tcProductDetailCode", header: "Incoming Product Detail Code", size: 240, minSize: 200, maxSize: 280 },
    { accessorKey: "isCertified", header: "Certified", size: 100, minSize: 90, maxSize: 120 },
    { accessorKey: "rawMaterialCode", header: "Incoming Raw Material Code", size: 230, minSize: 200, maxSize: 260 },
    ...(!directWeightMode
      ? [{ accessorKey: "rawMaterialPercentage", header: "Raw Material %", size: 140, minSize: 120, maxSize: 170 }]
      : []),
    {
      accessorKey: "calculatedWeight",
      header: "Incoming Raw Material Weight Used",
      size: 280,
      minSize: 240,
      maxSize: 320,
      Cell: ({ row }: any) => {
        const trace = Array.isArray(row.original.calculationTrace) ? row.original.calculationTrace : [];
        const key = row.original.rawMaterialKey || "";
        const fallbackValue = row.original.rawMaterialWeight ? String(row.original.rawMaterialWeight) : "";
        const value = weightsRef.current?.[key] ?? (row.original.calculatedWeight || fallbackValue);
        if (directWeightMode && isCertifiedIncomingRawMaterial(row.original)) {
          return (
            <TextField
              variant="outlined"
              size="small"
              value={value}
              onChange={(event) => changeRef.current?.(key, event.target.value)}
              sx={{ width: "120px", backgroundColor: "white" }}
              inputProps={{ type: "number", min: 0.01, step: "0.01" }}
            />
          );
        }
        if (directWeightMode) {
          return <Typography variant="body2">{value || "-"}</Typography>;
        }
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2">{row.original.calculatedWeight}</Typography>
            {trace.length > 0 && (
              <Tooltip
                placement="left"
                title={
                  <Box sx={{ p: 0.5 }}>
                    {trace.map((line: string) => (
                      <Typography key={line} variant="caption" display="block">
                        {line}
                      </Typography>
                    ))}
                  </Box>
                }
              >
                <InfoOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ], [directWeightMode]);

  if (loading) {
    // Show only the shared LoaderComponent while waiting for weight/raw data
    return (
      <Box sx={{ width: "100%", p: 2, display: 'flex', justifyContent: 'center' }}>
        <LoaderComponent />
      </Box>
    );
  }

  if (!rawMaterialDetails || rawMaterialDetails.length === 0) {
    return (
      <Box sx={{ width: "100%", p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          No raw material data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={READABLE_HEADER_TABLE_SX}>
      <CustomTable
        columns={rawMaterialColumns}
        data={rawMaterialDetails}
        enableTopToolbar={false}
        enableBottomToolbar={false}
      />
    </Box>
  );
});

const ProductNetWeight: React.FC<ComponentInfoProps> = ({ 
  formRef, 
  componentData, 
  setComponentData, 
  selectedArticles, 
  incomingTcId, 
  incomingAtcId,
  incomingTcType,
  standardsByArticle,
}) => {
  const [products, setProducts] = useState<ProductShipmentResponse[]>([]);
  const [netWeights, setNetWeights] = useState<{ [key: string]: string }>({});
  const [expanded, setExpanded] = useState<string[]>([]);
  const [rawMaterialData, setRawMaterialData] = useState<{ [key: string]: Omit<RawMaterialDetail, 'calculatedWeight'>[] }>({});
  const [rawMaterialWeights, setRawMaterialWeights] = useState<{ [productNo: string]: { [rawMaterialKey: string]: string } }>({});
  const [rawMaterialLoading, setRawMaterialLoading] = useState<{ [key: string]: boolean }>({});
  const [isLoading, setIsLoading] = useState(false);
  const hasSkippedInitialParentSync = useRef(false);
  
  const { TE_ID } = useParams();
  const { executeGet } = useApi();

  const effectiveTcId = incomingTcId || incomingAtcId;
  const incomingTypeUpper = String(incomingTcType || "").toUpperCase();
  // DAT-501 requires direct certified-raw-material weights only for TC v3.
  // FPF uses the same mandatory product net-weight input as the other
  // non-v3 incoming source types.
  const directWeightMode = incomingTypeUpper === "V3";
  const hideNetWeightInput = false;
  const [calcOpen, setCalcOpen] = useState<{ [key: string]: boolean }>({});
  const getArticleStandard = (productNo: string) =>
    formatStandards(
      standardsByArticle?.[productNo] ||
      componentData?.standards?.[productNo] ||
      rawMaterialData?.[productNo]?.map((row: any) => row.incomingStandard || row.standard)
    );
  const normalizeRawMaterialRows = (rows: any[] = []) =>
    filterIncomingRawMaterialsForSource(
      withIncomingRawMaterialKeys(rows.map((row: any) => ({
        ...row,
        incomingStandard: row.incomingStandard || row.standard || "",
        rawMaterialPercentage: toNumber(row.rawMaterialPercentage) || 100,
        tcProductNetShippingWeight: toNumber(row.tcProductNetShippingWeight),
        tcProductSupplementaryWeight: toNumber(row.tcProductSupplementaryWeight),
        tcProductAdditivesWeight: toNumber(row.tcProductAdditivesWeight),
      }))),
      incomingTypeUpper
    );
  const certifiedRawMaterialsWithKeys = (rows: any[] = []) =>
    filterCertifiedIncomingRawMaterials(withIncomingRawMaterialKeys(rows));

  const getSeededRawMaterials = (productNo: string) => {
    const rows = componentData?.rawMaterials?.[productNo];
    return Array.isArray(rows) ? normalizeRawMaterialRows(rows) : [];
  };

  useEffect(() => {
    if (componentData?.netWeights && Object.keys(componentData.netWeights).length > 0) {
      const allKeys = Object.keys(componentData.netWeights);
      const filteredKeys = Array.isArray(selectedArticles) && selectedArticles.length > 0
        ? allKeys.filter((k) => selectedArticles.includes(k))
        : allKeys;

      const initialWeights: { [key: string]: string } = {};
      filteredKeys.forEach((key) => {
        const value = componentData.netWeights[key];
        if (value !== undefined && value !== null) {
          initialWeights[key] = String(value);
        }
      });
      setNetWeights(initialWeights);

      if (products.length === 0) {
        const productsFromWeights = filteredKeys.map((key) => ({ tcProductNo: key }));
        setProducts(productsFromWeights);
      }
    }
  }, [componentData?.netWeights, JSON.stringify(selectedArticles)]);

  useEffect(() => {
    if (componentData?.rawMaterials && Object.keys(componentData.rawMaterials).length > 0) {
      
      if (Array.isArray(selectedArticles) && selectedArticles.length > 0) {
        const filtered: { [key: string]: Omit<RawMaterialDetail, 'calculatedWeight'>[] } = {};
        Object.entries(componentData.rawMaterials).forEach(([key, value]) => {
          if (selectedArticles.includes(key)) {
            filtered[key] = normalizeRawMaterialRows(value as any);
          }
        });
        setRawMaterialData(filtered);
      } else {
        const normalized = Object.fromEntries(
          Object.entries(componentData.rawMaterials).map(([key, value]) => [key, normalizeRawMaterialRows(value as any)])
        );
        setRawMaterialData(normalized as any);
      }
    }
  }, [componentData?.rawMaterials, JSON.stringify(selectedArticles)]);

  useEffect(() => {
    if (componentData?.rawMaterialWeights && Object.keys(componentData.rawMaterialWeights).length > 0) {
      const filtered = Array.isArray(selectedArticles) && selectedArticles.length > 0
        ? Object.fromEntries(Object.entries(componentData.rawMaterialWeights).filter(([key]) => selectedArticles.includes(key)))
        : componentData.rawMaterialWeights;
      const asStrings = Object.fromEntries(
        Object.entries(filtered as any).map(([productNo, values]) => [
          productNo,
          Object.fromEntries(Object.entries(values as any).map(([key, value]) => [key, String(value)])),
        ])
      );
      setRawMaterialWeights(asStrings as any);
    }
  }, [componentData?.rawMaterialWeights, JSON.stringify(selectedArticles)]);

  useEffect(() => {
    if (!Array.isArray(selectedArticles)) return;

    setNetWeights((prev) => {
      const entries = Object.entries(prev).filter(([k]) => selectedArticles.includes(k));
      return Object.fromEntries(entries);
    });

    
    setRawMaterialData((prev) => {
      const out: typeof prev = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (selectedArticles.includes(k)) out[k] = v;
      });
      return out;
    });

    
    setRawMaterialWeights((prev) => {
      const entries = Object.entries(prev).filter(([k]) => selectedArticles.includes(k));
      return Object.fromEntries(entries);
    });

    setExpanded((prev) => prev.filter((p) => selectedArticles.includes(p)));

    
    setProducts((prev) => prev.filter((p) => selectedArticles.includes(p.tcProductNo)));
  }, [JSON.stringify(selectedArticles)]);

  const fetchProducts = async (selected?: string[]) => {
    if (!effectiveTcId) return;
    
    setIsLoading(true);
    try {
      let productData: ProductShipmentResponse[] = [];
  
      if (selected && selected.length > 0) {
        productData = selected.map((art) => ({
          tcProductNo: art,
        }));
      } else if (incomingTcType) {
        const response = await executeGet(TCAppConfig.GET_INCOMING_TC_INFO, {
          incomingTcNoTcId: effectiveTcId,
          incomingTcType,
        });
        productData = unwrapIncomingProducts(response?.data?.response ?? response?.data)
          .map((item: any) => ({ tcProductNo: item.tcProductArticleNo || item.tcProductNo }))
          .filter((item: ProductShipmentResponse) => Boolean(item.tcProductNo));
      }
  
      setProducts(productData);

      // RMDF/FPF rows and balances are carried forward by the preceding
      // step. Seed them synchronously here so the mount-time API fallbacks
      // cannot win a race and replace valid data with [] / 0.
      setRawMaterialData((prev) => {
        const next = { ...prev };
        productData.forEach(({ tcProductNo }) => {
          const seededRows = getSeededRawMaterials(tcProductNo);
          if (seededRows.length > 0 && !next[tcProductNo]?.length) {
            next[tcProductNo] = seededRows;
          }
        });
        return next;
      });
      // Fetch only missing raw-material data. Source-specific rows seeded by
      // Step 2 remain authoritative for RMDF/FPF.
      const rmPromises = productData
        .filter((product) => getSeededRawMaterials(product.tcProductNo).length === 0)
        .map((product) => fetchRawMaterialData(product.tcProductNo));
  
      await Promise.all(rmPromises);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // In-flight/result caches so raw-material loaders share one incoming-source
  // request per article.
  const incomingInfoPromises = useRef<Record<string, Promise<{ details: any[] } | null>>>({});
  const rawMaterialPromises = useRef<Record<string, Promise<void>>>({});

  useEffect(() => {
    incomingInfoPromises.current = {};
    rawMaterialPromises.current = {};
  }, [effectiveTcId]);

  const fetchIncomingProductInfo = (productNo: string) => {
    const cached = incomingInfoPromises.current[productNo];
    if (cached) return cached;
    const request = fetchIncomingProductInfoUncached(productNo);
    incomingInfoPromises.current[productNo] = request;
    return request;
  };

  const fetchIncomingProductInfoUncached = async (productNo: string) => {
    if (!effectiveTcId || !incomingTcType) return null;
    try {
      const apiResponse = await coalesceRequest(
        `incoming-info:${effectiveTcId}:${productNo}:${incomingTcType}`,
        () =>
          executeGet(TCAppConfig.GET_INCOMING_TC_INFO, {
            incomingTcNoTcId: effectiveTcId,
            tcProductIncomingArticleNo: productNo,
            incomingTcType,
          })
      );
      const rows = unwrapIncomingProducts(apiResponse?.data?.response ?? apiResponse?.data);
      const matchingProducts = rows.filter((row: any) => String(row?.tcProductArticleNo || "") === productNo);
      const productsForArticle = matchingProducts.length > 0 ? matchingProducts : rows.slice(0, 1);
      if (productsForArticle.length === 0) return null;

      const storedRows = Array.isArray(componentData?.rawMaterials?.[productNo])
        ? componentData.rawMaterials[productNo]
        : [];
      const sourceDetails = normalizeRawMaterialRows(
        productsForArticle.flatMap((product: any) =>
          mapIncomingProductToRawDetails(product, incomingTypeUpper)
        )
      );
      const details = normalizeRawMaterialRows(
        mergeIncomingRawMaterialsForDisplay(sourceDetails, storedRows)
      );
      if (details.length > 0) {
        setRawMaterialData((prev) =>
          prev[productNo]?.length ? prev : { ...prev, [productNo]: details }
        );
      }
      return { details };
    } catch (err) {
      console.error(`Failed to fetch incoming source data for ${productNo}:`, err);
      return null;
    }
  };

  const fetchRawMaterialData = (productNo: string) => {
    const inFlight = rawMaterialPromises.current[productNo];
    if (inFlight) return inFlight;
    const request = fetchRawMaterialDataUncached(productNo);
    rawMaterialPromises.current[productNo] = request;
    return request;
  };

  const fetchRawMaterialDataUncached = async (productNo: string) => {
    if (!effectiveTcId) {
      console.warn(`fetchRawMaterialData: effectiveTcId missing for product ${productNo}`);
      return;
    }

    const seededRows = getSeededRawMaterials(productNo);
    if (seededRows.length > 0) {
      setRawMaterialData((prev) =>
        prev[productNo]?.length ? prev : { ...prev, [productNo]: seededRows }
      );
      return;
    }

    // If data already present, skip fetching
    if (rawMaterialData[productNo] && rawMaterialData[productNo].length > 0) {
      return;
    }
    const sourceInfo = await fetchIncomingProductInfo(productNo);
    if (!sourceInfo?.details?.length) {
      setRawMaterialData((prev) =>
        prev[productNo]?.length ? prev : { ...prev, [productNo]: [] }
      );
    }
  };

  // Fetch products when effectiveTcId or selectedArticles change
  useEffect(() => {
    if (effectiveTcId && selectedArticles && selectedArticles.length > 0) {
      fetchProducts(selectedArticles);
    }
  }, [effectiveTcId, JSON.stringify(selectedArticles)]);

  useEffect(() => {
    if (!Array.isArray(selectedArticles)) return;
  
    const newExpanded: string[] = [];
    selectedArticles.forEach(article => {
      if (rawMaterialData[article] && rawMaterialData[article].length > 0) {
        newExpanded.push(article);
      }
    });
  
    setExpanded(prev => {
      const merged = [...new Set([...prev, ...newExpanded])];
      return merged;
    });
  }, [rawMaterialData, JSON.stringify(selectedArticles)]);

  useEffect(() => {
    if (!directWeightMode) return;

    setRawMaterialWeights((prev) => {
      let changed = false;
      const next = { ...prev };

      Object.entries(rawMaterialData).forEach(([productNo, rows]) => {
        const current = { ...(next[productNo] || {}) };
        certifiedRawMaterialsWithKeys(rows).forEach((row) => {
          const key = row.rawMaterialKey;
          const storedWeight = toNumber(row.rawMaterialWeight);
          if (
            storedWeight > 0 &&
            !Object.prototype.hasOwnProperty.call(current, key)
          ) {
            current[key] = String(storedWeight);
            changed = true;
          }
        });
        if (Object.keys(current).length > 0) {
          next[productNo] = current;
        }
      });

      return changed ? next : prev;
    });
  }, [directWeightMode, rawMaterialData]);

  // Sync componentData back to parent
  useEffect(() => {
    if (!hasSkippedInitialParentSync.current) {
      hasSkippedInitialParentSync.current = true;
      return;
    }

    const numericWeights: { [key: string]: number } = {};
    Object.entries(netWeights).forEach(([productNo, weight]) => {
      const numWeight = Number(weight);
      if (!isNaN(numWeight) && numWeight > 0) {
        numericWeights[productNo] = numWeight;
      }
    });
    const newData = {
      netWeights: numericWeights,
      rawMaterials: rawMaterialData,
      rawMaterialWeights: Object.fromEntries(
        Object.entries(rawMaterialWeights).map(([productNo, values]) => [
          productNo,
          Object.fromEntries(
            Object.entries(values).map(([key, value]) => [key, Number(value) || 0])
          ),
        ])
      ),
      standards: standardsByArticle || componentData?.standards || {},
    };

    if (JSON.stringify(componentData) !== JSON.stringify(newData)) {
      setComponentData(newData);
    }
  }, [netWeights, rawMaterialData, rawMaterialWeights, standardsByArticle]);

  const toggleExpand = async (productNo: string) => {
    const isCurrentlyExpanded = expanded.includes(productNo);
  
    if (!isCurrentlyExpanded) {
      try {
        setExpanded((prev) => [...prev, productNo]);
        setRawMaterialLoading((prev) => ({ ...prev, [productNo]: true }));
  
        await fetchRawMaterialData(productNo);
        setRawMaterialLoading((prev) => ({ ...prev, [productNo]: false }));
      } catch (err) {
        console.error(`Error in toggleExpand for ${productNo}:`, err);
        setRawMaterialLoading((prev) => ({ ...prev, [productNo]: false }));
      }
    } else {
      setExpanded((prev) => prev.filter((p) => p !== productNo));
    }
  };

  const handleChange = (productNo: string, value: string) => {
    // TC v4 weights support up to five decimal places end to end.
    if (value !== "" && !/^\d*(?:\.\d{0,5})?$/.test(value)) {
      return;
    }
    setNetWeights((prev) => ({ ...prev, [productNo]: value }));
  };

  const handleRawMaterialWeightChange = (productNo: string, key: string, value: string) => {
    // TC v4 weights support up to five decimal places end to end.
    if (value !== "" && !/^\d*(?:\.\d{0,5})?$/.test(value)) {
      return;
    }

    const nextForProduct = {
      ...(rawMaterialWeights[productNo] || {}),
      [key]: value,
    };

    setRawMaterialWeights((prev) => ({
      ...prev,
      [productNo]: nextForProduct,
    }));
  };

  if (isLoading) {
    return <LoaderComponent />;
  }
  if (products.length === 0 ) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          No products available. Please select incoming products in the previous step.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {products.map((product) => {
        const productNo = product.tcProductNo;
        const isExpanded = expanded.includes(productNo);
    
        const currentNetWeight = Number(netWeights[productNo]) || 0;

        const baseDetails = filterIncomingRawMaterialsForSource(
          rawMaterialData[productNo] || [],
          incomingTypeUpper
        );

        return (
          <Box key={productNo}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1,
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                boxShadow: "0px 2px 1px -1px rgba(0,0,0,0.2)",
              }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {/* FPF references are lots, not product articles (86ajfgrcn). */}
                Incoming {incomingTypeUpper.startsWith("FPF") ? "Lot" : "Product Article"} No.: {productNo}
              </Typography>
              <Typography variant="body2" sx={{ mr: 1 }}>
                {incomingTypeUpper.startsWith("FPF") ? "Lot" : "Product"} Net Weight Used:
              </Typography>
              {hideNetWeightInput ? (
                <Typography variant="body2" sx={{ width: "100px", textAlign: "right", fontWeight: 600 }}>
                  {netWeights[productNo] || "-"}
                </Typography>
              ) : (
                <TextField
                  variant="outlined"
                  size="small"
                  value={netWeights[productNo] || ""}
                  onChange={(e) => handleChange(productNo, e.target.value)}
                  sx={{ width: "100px", backgroundColor: "white" }}
                  inputProps={{ type: "number", min: 0.01, step: "0.01" }}
                />
              )}
              {!directWeightMode && (
                <Tooltip title="Show how the weights are calculated">
                  <IconButton
                    size="small"
                    onClick={() => setCalcOpen((prev) => ({ ...prev, [productNo]: !prev[productNo] }))}
                    sx={{ ml: 1 }}
                    color={calcOpen[productNo] ? "primary" : "default"}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton onClick={() => toggleExpand(productNo)} sx={{ ml: 1 }}>
                <ArrowDropDownIcon sx={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" }} />
              </IconButton>
            </Box>
            {!directWeightMode && calcOpen[productNo] && (() => {
              const primary = baseDetails[0];
              if (!primary) {
                return (
                  <Box sx={{ p: 2, mt: 1, backgroundColor: "#fafafa", border: "1px dashed #ccc", borderRadius: "4px" }}>
                    <Typography variant="body2" color="textSecondary">
                      Expand the product to load raw material data before viewing the calculation.
                    </Typography>
                  </Box>
                );
              }
              const overview = calculateSplitDetails(currentNetWeight, 100, primary);
              return (
                <Box sx={{ p: 2, mt: 1, backgroundColor: "#fafafa", border: "1px dashed #ccc", borderRadius: "4px" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Calculation for {productNo}
                  </Typography>
                  <Typography variant="body2">
                    {`Source product: net ${formatTcV4Decimal(overview.sourceNet)}, supplementary ${formatTcV4Decimal(overview.sourceSupplementary)}, additives ${formatTcV4Decimal(overview.sourceAdditives)}`}
                  </Typography>
                  <Typography variant="body2">
                    {`Net weight used: ${formatTcV4Decimal(currentNetWeight)}`}
                  </Typography>
                  <Typography variant="body2">
                    {`Supplementary used: ${formatTcV4Decimal(overview.sourceSupplementary)} / ${formatTcV4Decimal(overview.sourceNet)} x ${formatTcV4Decimal(currentNetWeight)} = ${formatTcV4Decimal(overview.supplementaryUsed)}`}
                  </Typography>
                  {overview.sourceAdditives > 0 && (
                    <Typography variant="body2">
                      {`Additives used: ${formatTcV4Decimal(overview.sourceAdditives)} / ${formatTcV4Decimal(overview.sourceNet)} x ${formatTcV4Decimal(currentNetWeight)} = ${formatTcV4Decimal(overview.additivesUsed)}`}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {`Product weight for split: ${formatTcV4Decimal(currentNetWeight)} - ${formatTcV4Decimal(overview.supplementaryUsed)}${overview.sourceAdditives > 0 ? ` - ${formatTcV4Decimal(overview.additivesUsed)}` : ""} = ${formatTcV4Decimal(overview.productWeightForSplit)}`}
                  </Typography>
                  {baseDetails.map((item, index) => {
                    const split = calculateSplitDetails(currentNetWeight, item.rawMaterialPercentage, item);
                    return (
                      <Typography variant="body2" key={item.rawMaterialKey || index}>
                        {`${item.rawMaterialCode || "RM"}: ${formatTcV4Decimal(split.productWeightForSplit)} x ${formatTcV4Decimal(item.rawMaterialPercentage || 0)}% = ${formatTcV4Decimal(split.calculatedWeight)}`}
                      </Typography>
                    );
                  })}
                </Box>
              );
            })()}
       
            {isExpanded && (
              <RenderRawMaterialPanel 
                row={{ original: product }} 
                rawMaterialDetails={baseDetails.map((item, index) => {
                  const detailKey = item.rawMaterialKey || rawMaterialKey(item, index);
                  const incomingStandard = item.incomingStandard || getArticleStandard(productNo);
                  if (directWeightMode) {
                    return {
                      ...item,
                      incomingStandard,
                      rawMaterialKey: detailKey,
                      calculatedWeight: rawMaterialWeights[productNo]?.[detailKey] || "",
                    };
                  }

                  const splitDetails = calculateSplitDetails(currentNetWeight, item.rawMaterialPercentage, item);
                  return {
                    ...item,
                    incomingStandard,
                    rawMaterialKey: detailKey,
                    calculatedWeight: roundTcV4Decimal(splitDetails.calculatedWeight).toString(),
                    calculationTrace: [
                      `Product net weight used: ${formatTcV4Decimal(currentNetWeight)}`,
                      `Source net weight: ${formatTcV4Decimal(splitDetails.sourceNet)}`,
                      `Source supplementary weight: ${formatTcV4Decimal(splitDetails.sourceSupplementary)}`,
                      `Source additives weight: ${formatTcV4Decimal(splitDetails.sourceAdditives)}`,
                      `Supplementary used: ${formatTcV4Decimal(splitDetails.supplementaryUsed)}`,
                      `Additives used: ${formatTcV4Decimal(splitDetails.additivesUsed)}`,
                      `Product weight for RM split: ${formatTcV4Decimal(splitDetails.productWeightForSplit)}`,
                      `RM weight: ${formatTcV4Decimal(splitDetails.productWeightForSplit)} x ${formatTcV4Decimal(item.rawMaterialPercentage || 0)}% = ${formatTcV4Decimal(splitDetails.calculatedWeight)}`,
                    ],
                  };
                })}
                loading={!!rawMaterialLoading[productNo]}
                directWeightMode={directWeightMode}
                rawMaterialWeights={rawMaterialWeights[productNo] || {}}
                onDirectWeightChange={(key, value) => handleRawMaterialWeightChange(productNo, key, value)}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ProductNetWeight;
